import React, { useState } from "react";
import {
  Badge,
  Dropdown,
  Button,
  List,
  Typography,
  Space,
  Switch,
  Divider,
} from "antd";
import { Icon } from "@iconify/react";
import {
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from "../../hooks/useNotifications";
import { useNavigate } from "react-router-dom";
import { useNotificationContext } from "../../context/NotificationContext";

const { Text } = Typography;

const NotificationBell: React.FC = () => {
  const {
    notifications,
    unreadCount,
    soundEnabled,
    setSoundEnabled,
    // isConnected,
    refreshNotifications,
  } = useNotificationContext();

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const { mutate: markAsRead } = useMarkNotificationAsRead();
  const { mutate: markAllAsRead, isPending: isMarkingAllAsRead } =
    useMarkAllNotificationsAsRead();
  const navigate = useNavigate();

  // Get recent notifications (last 5)
  const recentNotifications = notifications.slice(0, 5);

  const handleNotificationClick = (notificationId: string) => {
    markAsRead(notificationId);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleViewAllNotifications = () => {
    setDropdownVisible(false);
    navigate("/notifications");
  };

  const handleRefresh = () => {
    refreshNotifications();
  };

  const formatNotificationTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getNotificationIcon = (message: string) => {
    if (message.toLowerCase().includes("order")) return "🛒";
    if (message.toLowerCase().includes("table")) return "🪑";
    if (message.toLowerCase().includes("inventory")) return "📦";
    if (message.toLowerCase().includes("schedule")) return "📅";
    return "🔔";
  };

  const dropdownContent = (
    <div
      className="notification-dropdown-card shadow-xl border-0 rounded-xl overflow-hidden bg-white text-secondary"
      style={{ width: 380, maxHeight: 500, padding: 0 }}
    >
      {/* Header */}
      <div className="notification-header px-5 py-4 bg-gradient-to-r from-secondary to-primary text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-white">
            <Icon icon="heroicons:bell" className="text-xl" />
            <h1 className="mb-0 ">Notifications</h1>
          </div>
          {/* <div className="flex items-center space-x-2">
            <Icon
              icon={isConnected ? "heroicons:wifi" : "heroicons:wifi-slash"}
              className={`text-sm ${
                isConnected ? "text-green-300" : "text-red-300"
              }`}
            />
            <div
              className={`w-2 h-2 rounded-full ${
                isConnected ? "bg-green-300" : "bg-red-300"
              }`}
            />
            <Text className="text-xs text-white opacity-90">
              {isConnected ? "Live" : "Offline"}
            </Text>
          </div> */}
        </div>

        <div className="flex items-center justify-between mt-3">
          <Text className="text-white opacity-90 text-sm">
            {unreadCount > 0 ? `${unreadCount} unread` : "All caught up! 🎉"}
          </Text>

          <Space size="small">
            <Button
              type="text"
              size="small"
              className="text-white border-white hover:bg-white hover:text-indigo-600 transition-all duration-200"
              onClick={handleRefresh}
            >
              <Icon icon="heroicons:arrow-path" className="text-sm" />
            </Button>
            {unreadCount > 0 && (
              <Button
                type="text"
                size="small"
                loading={isMarkingAllAsRead}
                onClick={handleMarkAllAsRead}
                className="text-white border-white hover:bg-white hover:text-indigo-600 transition-all duration-200"
              >
                <Icon icon="heroicons:check" className="text-sm mr-1" />
                Read all
              </Button>
            )}
          </Space>
        </div>
      </div>

      {/* Notifications List */}
      <div
        className="notification-list"
        style={{ maxHeight: 300, overflowY: "auto" }}
      >
        {recentNotifications.length > 0 ? (
          <List
            itemLayout="horizontal"
            dataSource={recentNotifications}
            renderItem={(notification) => (
              <List.Item
                className={`notification-item px-4 py-3 hover:bg-gray-50 cursor-pointer border-none transition-all duration-200 ${
                  !notification.read_at
                    ? "unread-notification"
                    : "read-notification"
                }`}
                onClick={() => handleNotificationClick(notification.id)}
                style={{
                  borderLeft: !notification.read_at
                    ? "4px solid #667eea"
                    : "4px solid transparent",
                  background: !notification.read_at ? "#f8f9ff" : "white",
                }}
              >
                <List.Item.Meta
                  avatar={
                    <div
                      className="notification-avatar flex items-center justify-center text-lg rounded-full"
                      style={{
                        width: 40,
                        height: 40,
                        background: !notification.read_at
                          ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                          : "#f5f5f5",
                      }}
                    >
                      {!notification.read_at
                        ? "🔔"
                        : getNotificationIcon(notification.data.message)}
                    </div>
                  }
                  title={
                    <div className="flex items-start justify-between">
                      <Text
                        className={`text-sm leading-tight ${
                          !notification.read_at
                            ? "font-semibold text-gray-900"
                            : "text-gray-600"
                        }`}
                        ellipsis={{ tooltip: notification.data.message }}
                        style={{ maxWidth: "240px" }}
                      >
                        {notification.data.message}
                      </Text>
                      {!notification.read_at && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 flex-shrink-0 animate-pulse" />
                      )}
                    </div>
                  }
                  description={
                    <div className="flex items-center space-x-2 mt-1">
                      <Icon
                        icon="heroicons:clock"
                        className="text-xs text-gray-400"
                      />
                      <Text type="secondary" className="text-xs">
                        {formatNotificationTime(notification.created_at)}
                      </Text>
                      {notification.read_at && (
                        <>
                          <span className="text-gray-300">•</span>
                          <Icon
                            icon="heroicons:eye"
                            className="text-xs text-gray-400"
                          />
                          <Text type="secondary" className="text-xs">
                            Read
                          </Text>
                        </>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <div className="p-8 text-center">
            <div className="text-6xl mb-4">🔕</div>
            <Text type="secondary" className="text-base">
              No notifications yet
            </Text>
            <div className="mt-2">
              <Text type="secondary" className="text-sm">
                We'll notify you when something important happens
              </Text>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}

      <>
        <Divider className="my-0" />
        <div className="notification-footer p-3 text-center bg-gray-50">
          <Button
            type="link"
            onClick={handleViewAllNotifications}
            className="text-purple-600 font-medium hover:text-purple-700"
          >
            View all notifications →
          </Button>
        </div>
      </>

      {/* Sound Settings */}
      <div className="p-3 border-t bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon
              icon={
                soundEnabled
                  ? "heroicons:speaker-wave"
                  : "heroicons:speaker-x-mark"
              }
              className={soundEnabled ? "text-purple-600" : "text-gray-400"}
            />
            <Text className="text-sm">Sound alerts</Text>
          </div>
          <Switch
            size="small"
            checked={soundEnabled}
            onChange={setSoundEnabled}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="notification-bell-container">
      {/* Add shake animation keyframes */}
      <style>
        {`
          @keyframes bellShake {
            0%, 50%, 100% { transform: rotate(0deg); }
            10%, 30% { transform: rotate(-10deg); }
            20%, 40% { transform: rotate(10deg); }
          }
          .notification-bell-shake {
            animation: bellShake 1s ease-in-out infinite;
          }
        `}
      </style>

      <Dropdown
        overlay={dropdownContent}
        trigger={["click"]}
        placement="bottomRight"
        open={dropdownVisible}
        onOpenChange={setDropdownVisible}
        overlayClassName="notification-dropdown-overlay"
      >
        <Button
          type="text"
          className={`notification-bell-button flex items-center justify-center relative rounded-full transition-all duration-300 ${
            unreadCount > 0 ? "bg-indigo-50" : ""
          }`}
          style={{
            width: 44,
            height: 44,
          }}
        >
          <Badge
            count={unreadCount}
            size="small"
            offset={[-6, 6]}
            style={{
              backgroundColor: "#667eea",
              boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
            }}
          >
            <Icon
              icon="heroicons:bell"
              className={`text-secondary transition-all duration-300 ${
                unreadCount > 0
                  ? "notification-bell-shake text-2xl text-indigo-600"
                  : "text-xl"
              }`}
              style={{
                filter:
                  unreadCount > 0
                    ? "drop-shadow(0 2px 4px rgba(102, 126, 234, 0.3))"
                    : "none",
              }}
            />
          </Badge>
        </Button>
      </Dropdown>
    </div>
  );
};

export default NotificationBell;
